import { CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/useAuth';
import { useFrontendConfig } from '../context/FrontendConfigContext';
import { slugifyBrandClient } from '../utils/gymPublicPath';
import api, { authService } from '../services/api';
import AuthLayout from './AuthLayout';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import TermsOfServiceModal from './TermsOfServiceModal';
import RegisterPlanPickerModal from './RegisterPlanPickerModal';
import PhoneField from './ui/PhoneField';
import { getSanitizedPublicBrandName } from '../utils/branding';
import { fetchPaymentMethods, openWompiCheckout } from '../utils/paymentMethods';
import { planMatchesInviteAllowList } from '../utils/invitePlanMatch';
import { resolvePublicApiBase } from '../utils/gymPublicPath';
import { setShellToken, clearShellToken, getShellToken } from '../utils/shellToken';
import { formatCycleDate, filterPurchasableCycles, pickDefaultCycleId } from '../utils/cycleUtils';
import {
  planCyclesIncluded,
  consecutiveCyclePack,
} from '../utils/planCyclePack';

const SERVICES = [
  { id: 'd28d', label: 'Fit Live · Programa', desc: 'Programas Vital, Pancitas o Virtual' },
  { id: 'food', label: 'FOOD PLAN', desc: 'Nutrición con coach — acceso desde el ecosistema (/food)' },
  { id: 'training', label: 'Entrenadores', desc: 'Rutinas y seguimiento con coach' },
];

const MODULE_PRESETS = {
  d28d: { d28d: true, live_classes: true },
  food: {
    food_plan: true,
    nutrition: true,
    d28d: false,
    gym: false,
    live_classes: false,
    training: false,
  },
  training: { training: true },
};

function readApiError(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  return data?.error || data?.message || fallback;
}

function inferRegisterField(message = '') {
  const m = String(message).toLowerCase();
  if (/correo|email|e-mail|registrad/.test(m)) return 'email';
  if (/confirm.*correo|correos no coinciden|correos electrónicos no coinciden/.test(m)) return 'emailConfirm';
  if (/contraseñ|password/.test(m) && /coincid/.test(m)) return 'confirmPassword';
  if (/contraseñ|password/.test(m)) return 'password';
  if (/tel[eé]fono/.test(m)) return 'teléfono';
  if (/g[eé]nero/.test(m)) return 'genero';
  if (/peso|altura/.test(m)) return 'peso';
  if (/nombre/.test(m)) return 'nombre';
  if (/c[oó]digo promocional|promocional/.test(m)) return 'promo_code';
  if (/c[oó]digo|invitac/.test(m)) return 'inviteCode';
  if (/ciclo/.test(m)) return 'cycle';
  if (/plan/.test(m)) return 'plan';
  if (/programa/.test(m)) return 'program';
  if (/servicio/.test(m)) return 'service';
  if (/moneda|cop|usd/.test(m)) return 'currency';
  if (/privacidad|t[eé]rminos|condiciones/.test(m)) return 'policies';
  if (/m[eé]todo de pago|pago/.test(m)) return 'metodoPago';
  return null;
}

function FieldNote({ message }) {
  if (!message) return null;
  return (
    <p className="text-xs text-rose-300 mt-1" role="alert">
      {message}
    </p>
  );
}

function readPlanList(payload) {
  return Array.isArray(payload) ? payload : (payload?.data || []);
}

/** Landing D28D: ?register=1&service=d28d&program=virtual_d28d&plan=basico */
function readLandingRegisterPreset() {
  try {
    const q = new URLSearchParams(window.location.search);
    if (String(q.get('code') || '').trim()) return null;
    const register = String(q.get('register') || '').trim().toLowerCase();
    if (register !== '1' && register !== 'true') return null;
    const serviceQ = String(q.get('service') || '').trim().toLowerCase();
    const programQ = String(q.get('program') || '').trim();
    const planQ = String(q.get('plan') || '').trim().toLowerCase();
    const orgQ = String(q.get('coach') || q.get('org') || '').trim();
    const service = serviceQ === 'food' ? 'food' : (serviceQ === 'training' ? 'training' : 'd28d');
    return {
      service,
      org: orgQ,
      programId: service === 'food' ? (programQ || '') : (service === 'd28d' ? (programQ || 'virtual_d28d') : ''),
      plan: planQ || 'basico',
    };
  } catch {
    return null;
  }
}

function stepAfterInvite(ctx) {
  if (!ctx) return 1;
  if (ctx.type === 'trainer') return 2;
  const planIds = Array.isArray(ctx.available_plan_ids) ? ctx.available_plan_ids : [];
  const hasDefaultPlan = Boolean(ctx.suggested_plan) || planIds.length === 1;
  // Sede / gym con plan ya asignado → directo a datos (plan se preselecciona).
  if (ctx.is_sede) {
    if (planIds.length >= 1 || hasDefaultPlan) return 5;
    const ids = Array.isArray(ctx.program_ids) && ctx.program_ids.length
      ? ctx.program_ids
      : (ctx.program_id ? [ctx.program_id] : []);
    return ids.length > 1 ? 2 : 5;
  }
  if (ctx.type === 'gym') {
    if (hasDefaultPlan) return 5;
    if (planIds.length > 1) return 3;
    if (planIds.length === 1) return 5;
  }
  if (ctx.skip_member_payment) {
    const ids = Array.isArray(ctx.program_ids) && ctx.program_ids.length
      ? ctx.program_ids
      : (ctx.program_id ? [ctx.program_id] : []);
    return ids.length > 1 ? 2 : 5;
  }
  if (hasDefaultPlan && ctx.suggested_plan) return 5;
  return 3;
}

export default function RegisterCommercialWizard({ onSwitchToLogin, initialEntryMode = 'direct' }) {
  const { register } = useAuth();
  const { brandName } = useFrontendConfig();
  const displayBrand = getSanitizedPublicBrandName(brandName);
  const [step, setStep] = useState(() => (
    readLandingRegisterPreset()?.service === 'food' ? 5 : 1
  ));
  const [service, setService] = useState(() => {
    const svc = readLandingRegisterPreset()?.service;
    return svc === 'food' || svc === 'training' ? svc : 'd28d';
  });
  const [programId, setProgramId] = useState('');
  const [programs, setPrograms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [usingGlobalPlanFallback, setUsingGlobalPlanFallback] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [currency, setCurrency] = useState('COP');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    emailConfirm: '',
    password: '',
    confirmPassword: '',
    teléfono: '',
    fecha_nacimiento: '',
    promo_code: '',
    peso: '',
    altura: '',
    genero: '',
    objetivo: '',
    tiene_restricciones: false,
    restricciones_detalles: '',
    soy_mama_posparto: false,
    metodoPago: 'wompi_online',
    partner_nombre: '',
    partner_email: '',
    partner_emailConfirm: '',
    partner_password: '',
    partner_confirmPassword: '',
    partner_teléfono: '',
    partner_genero: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPartnerPassword, setShowPartnerPassword] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [entryMode, setEntryMode] = useState(initialEntryMode);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteContext, setInviteContext] = useState(null);
  const [inviteServiceChoice, setInviteServiceChoice] = useState(() => (
    readLandingRegisterPreset()?.service === 'training' ? 'training_client' : ''
  ));
  const [validatingCode, setValidatingCode] = useState(() => {
    try {
      return Boolean(new URLSearchParams(window.location.search).get('code')?.trim());
    } catch {
      return false;
    }
  });
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [reactivateMode, setReactivateMode] = useState(false);
  const autoValidatedRef = useRef(false);
  const landingPreset = useMemo(() => readLandingRegisterPreset(), []);
  const landingPresetApplied = useRef(false);
  const isFoodLandingRegister = landingPreset?.service === 'food';
  const isTrainingOrgRegister = landingPreset?.service === 'training';

  useEffect(() => {
    if (landingPreset?.service !== 'training' || !landingPreset.org) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${resolvePublicApiBase()}/trainers/public/by-slug/${encodeURIComponent(landingPreset.org)}`,
        );
        const data = await res.json().catch(() => ({}));
        const trainer = data?.trainer;
        if (cancelled || !trainer?.id) {
          if (!cancelled) setError('No encontramos la marca de este entrenador.');
          return;
        }
        setInviteContext({
          type: 'trainer',
          trainer_id: trainer.id,
          brand_slug: trainer.brand_slug || landingPreset.org,
          brand_name: trainer.brand_name || trainer.nombre || '',
          label: trainer.brand_name || trainer.nombre || '',
          module_access: { training: true },
        });
        if (trainer.invite_code) {
          setInviteCode(String(trainer.invite_code).trim().toUpperCase());
          setEntryMode('invite');
        }
        setInviteServiceChoice('training_client');
        setService('training');
        setStep(5);
      } catch {
        if (!cancelled) setError('No pudimos abrir el registro de este entrenador.');
      }
    })();
    return () => { cancelled = true; };
  }, [landingPreset]);

  const goWizardBack = (fallbackStep) => {
    if (isFoodLandingRegister || isTrainingOrgRegister) {
      setStep(5);
      return;
    }
    setStep(fallbackStep);
  };

  useEffect(() => {
    if (!landingPreset) return;
    setService(landingPreset.service);
    if (landingPreset.programId) setProgramId(landingPreset.programId);
  }, [landingPreset]);

  useEffect(() => {
    try {
      const reactivate = String(localStorage.getItem('reactivateEmail') || '').trim().toLowerCase();
      const prefill = String(localStorage.getItem('prefillEmail') || '').trim().toLowerCase();
      const email = reactivate || prefill;
      if (!email) return;
      setFormData((prev) => ({
        ...prev,
        email: prev.email || email,
        emailConfirm: prev.emailConfirm || email,
      }));
      if (reactivate) {
        setReactivateMode(true);
        setService('food');
        try { localStorage.removeItem('reactivateEmail'); } catch { /* noop */ }
      }
    } catch { /* noop */ }
  }, []);

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const focusRegisterField = (field) => {
    if (!field || typeof document === 'undefined') return;
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-field="${field}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = el.matches('input, select, textarea, button')
        ? el
        : el.querySelector('input, select, textarea, button');
      focusable?.focus?.();
    });
  };

  const reportError = (message, fieldHint = null) => {
    const field = fieldHint || inferRegisterField(message);
    setError(message);
    if (field) {
      setFieldErrors({ [field]: message });
      focusRegisterField(field);
    } else {
      setFieldErrors({});
    }
  };

  const isTrainerInvite = inviteContext?.type === 'trainer';
  const isNicolasAnchorInvite = inviteContext?.is_nicolas_anchor === true;
  const isNewCoachTrainingReg = isTrainerInvite && inviteServiceChoice === 'training';
  const isJabelTrainingPromoReg = isTrainerInvite
    && String(inviteContext?.brand_slug || '').trim() === 'jabel-coaching-online'
    && (inviteServiceChoice === 'training_client' || inviteServiceChoice === 'training');
  /** Alta de coach (marca blanca) o cliente training: sin Wompi del cliente; coach activa después. */
  const skipCoachTrainingPlan = isNewCoachTrainingReg;

  const INVITE_TRAINER_SERVICES = [
    {
      id: 'training_client',
      label: 'Entrenamiento',
      desc: 'Rutinas con tu coach. Quedas pendiente de activación; tu entrenador paga y activa desde su panel.',
    },
    { id: 'food', label: 'FOOD PLAN', desc: 'Nutrición con coach — acceso desde el ecosistema' },
  ];

  const INVITE_NICOLAS_TRAINER_SERVICES = [
    {
      id: 'training_client',
      label: 'Cliente — Entrenamiento',
      desc: 'Rutinas con coach Nicolás del Río. Quedas pendiente; el coach activa desde Activar clientes.',
    },
    { id: 'food', label: 'FOOD PLAN', desc: 'Nutrición con coach — acceso desde el ecosistema' },
  ];

  const inviteTrainerServices = isNicolasAnchorInvite
    ? INVITE_NICOLAS_TRAINER_SERVICES
    : INVITE_TRAINER_SERVICES;

  const skipMemberPayment = inviteContext?.skip_member_payment === true;

  useEffect(() => {
    if (autoValidatedRef.current) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code')?.trim();
      if (!code) return;
      autoValidatedRef.current = true;
      setEntryMode('invite');
      setInviteCode(code.toUpperCase());
      setValidatingCode(true);
      setError('');
      api.post('/auth/resolve-invite', { code }, { skipShellAuth: true })
        .then((res) => {
          const ctx = res.data?.data;
          if (!ctx) {
            setError('Código no válido');
            setStep(1);
            return;
          }
          setInviteContext(ctx);
          const svc = String(params.get('service') || '').trim().toLowerCase();
          if (ctx.type === 'trainer' && svc === 'training') {
            setInviteServiceChoice('training_client');
            setService('training');
            setStep(5);
          } else {
            setStep(stepAfterInvite(ctx));
          }
          if (ctx.program_id) setProgramId(ctx.program_id);
        })
        .catch((err) => {
          setError(readApiError(err, 'No pudimos validar el código'));
          setStep(1);
        })
        .finally(() => setValidatingCode(false));
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    setEntryMode(initialEntryMode);
    if (initialEntryMode === 'invite') {
      // Con ?code= en la URL, el efecto de auto-validación define el paso (datos/plan).
      try {
        const code = new URLSearchParams(window.location.search).get('code')?.trim();
        if (code) return;
      } catch { /* noop */ }
      setStep(1);
      setError('');
    }
  }, [initialEntryMode]);

  const effectiveService = useMemo(() => {
    if (isTrainerInvite) {
      if (inviteServiceChoice === 'training_client') return 'training';
      return inviteServiceChoice;
    }
    if (inviteContext) {
      const ma = inviteContext.module_access || {};
      if (ma.training && !ma.d28d && !ma.food_plan) return 'training';
      if ((ma.food_plan || ma.nutrition) && !ma.d28d && !ma.training) return 'food';
      return 'd28d';
    }
    return service;
  }, [inviteContext, inviteServiceChoice, isTrainerInvite, service]);

  /** Cliente Training (código coach): pendiente de activación; Wompi lo paga el coach. */
  const isTrainingCoachClientPending = effectiveService === 'training'
    && isTrainerInvite
    && inviteServiceChoice === 'training_client';
  const isTrainingPendingActivation = effectiveService === 'training'
    && isTrainerInvite
    && (inviteServiceChoice === 'training' || inviteServiceChoice === 'training_client');
  const skipTrainingClientPlan = skipCoachTrainingPlan || isTrainingCoachClientPending;

  const effectiveProgramId = useMemo(() => {
    if (programId) return programId;
    return inviteContext?.program_id || '';
  }, [inviteContext, programId]);

  const invitePrograms = useMemo(() => {
    const active = programs.filter((p) => p.active !== false);
    if (!inviteContext?.program_ids?.length) return active;
    const allowed = new Set(inviteContext.program_ids.map(String));
    return active.filter((p) => allowed.has(String(p.id)));
  }, [programs, inviteContext]);

  const assignedProgramLabel = useMemo(() => {
    if (!effectiveProgramId) return null;
    return programs.find((p) => String(p.id) === String(effectiveProgramId))?.name || effectiveProgramId;
  }, [programs, effectiveProgramId]);

  const paymentModuleCode = useMemo(() => {
    if (inviteContext?.type === 'gym' || inviteContext?.plan_scope === 'gym') return 'gym';
    const svc = effectiveService;
    if (!svc) return 'd28d';
    return svc === 'food' ? 'food' : svc === 'training' ? 'training' : 'd28d';
  }, [effectiveService, inviteContext]);

  const sedeLabel = useMemo(() => {
    if (inviteContext?.gym_name) return inviteContext.gym_name;
    if (inviteContext?.type === 'gym' && inviteContext?.label) {
      return inviteContext.label.replace(/^Gimnasio:\s*/i, '');
    }
    return null;
  }, [inviteContext]);

  useEffect(() => {
    const params = {};
    if (inviteContext?.gym_id) params.gym_id = inviteContext.gym_id;
    api.get('/programs/public', { params, skipShellAuth: true })
      .then((res) => setPrograms(res.data?.data || []))
      .catch((err) => {
        console.error('Error cargando programas para registro:', err);
        setPrograms([]);
        setError('No se pudieron cargar los programas. Recarga la página o intenta más tarde.');
      });
  }, [inviteContext?.gym_id]);

  useEffect(() => {
    if (!paymentModuleCode || skipMemberPayment || skipTrainingClientPlan) return;
    setPaymentMethodsLoading(true);
    fetchPaymentMethods(paymentModuleCode, { programId: effectiveProgramId || undefined })
      .then((methods) => {
        let filtered = methods;
        setPaymentMethods(filtered);
        const defaultMethod = effectiveService === 'training'
          ? (filtered.find((m) => m.id === 'pago_sede')?.id || filtered[0]?.id)
          : filtered[0]?.id;
        if (filtered.length && defaultMethod && formData.metodoPago !== defaultMethod) {
          setFormData((prev) => ({ ...prev, metodoPago: defaultMethod }));
        }
      })
      .catch(() => setPaymentMethods([]))
      .finally(() => setPaymentMethodsLoading(false));
  }, [paymentModuleCode, effectiveProgramId, effectiveService, skipMemberPayment, skipTrainingClientPlan]);

  useEffect(() => {
    if (!skipMemberPayment) return;
    setFormData((prev) => (prev.metodoPago === 'registro_sede' ? prev : { ...prev, metodoPago: 'registro_sede' }));
  }, [skipMemberPayment]);

  const filteredPlans = useMemo(() => {
    const svc = effectiveService;
    if (!svc) return [];
    const isGymInvite = inviteContext?.type === 'gym' && inviteContext.gym_id != null;
    const isSedeInvite = Boolean(inviteContext?.is_sede || inviteContext?.parent_gym_id);
    const useGlobalGymPlans = isGymInvite && usingGlobalPlanFallback && !isSedeInvite;
    const gymId = isGymInvite ? Number(inviteContext.gym_id) : null;
    const gymOwnedActive = isGymInvite && !useGlobalGymPlans
      ? plans.filter(
        (p) => p.activo !== false && p.visible !== false
          && String(p.kind) === svc
          && Number(p.gym_id) === gymId,
      )
      : [];

    let list = plans.filter((p) => {
      if (p.activo === false || p.visible === false) return false;
      if (p?.module_access?.__catalog_deleted === true) return false;
      if (String(p.kind) !== svc) return false;
      if (isSedeInvite && p.for_sedes !== true) return false;
      if (isGymInvite) {
        if (useGlobalGymPlans) return p.gym_id == null;
        if (gymOwnedActive.length) return Number(p.gym_id) === gymId;
        return true;
      }
      // Registro gratis / directo: todos los planes D28D de plataforma (no filtrar por programa).
      if (svc === 'd28d' && !inviteContext) {
        return p.gym_id == null && p.for_sedes !== true;
      }
      if (svc === 'd28d' && effectiveProgramId) {
        return String(p.program_id) === String(effectiveProgramId);
      }
      if (svc === 'training') {
        if (isTrainerInvite && inviteServiceChoice === 'food') return false;
        if (isTrainerInvite && !inviteServiceChoice) return false;
        const audience = (isTrainerInvite && inviteServiceChoice === 'training') ? 'coach' : 'client';
        return String(p.training_audience || (audience === 'coach' ? 'coach' : 'client')) === audience;
      }
      return true;
    });
    if (inviteContext?.plan_scope && !isTrainerInvite && !isGymInvite) {
      const scope = inviteContext.plan_scope;
      if (inviteContext.type === 'program' && inviteContext.program_id) {
        const pid = String(inviteContext.program_id);
        list = list.filter((p) => String(p.program_id || '') === pid);
      } else if (scope === 'trainer') {
        list = list.filter((p) => {
          const pid = String(p.program_id || '').toLowerCase();
          return pid === 'virtual_d28d' || pid === 'vital' || !p.program_id;
        });
      } else if (scope === 'd28d') {
        list = list.filter((p) => {
          const pid = String(p.program_id || '').toLowerCase();
          return ['virtual_d28d', 'pancitas', 'vital'].includes(pid);
        });
      }
    }
    if (Array.isArray(inviteContext?.available_plan_ids) && inviteContext.available_plan_ids.length) {
      const allowed = inviteContext.available_plan_ids;
      const scoped = list.filter((p) => planMatchesInviteAllowList(p, allowed));
      // Catálogo por gym_id ya es la fuente de verdad; no aplicar lista stale del invite.
      if (!(isGymInvite && !useGlobalGymPlans)) {
        list = scoped.length ? scoped : list;
      }
    }
    return list;
  }, [plans, effectiveService, effectiveProgramId, inviteContext, isTrainerInvite, inviteServiceChoice, usingGlobalPlanFallback]);

  const cyclesForSelectedPlan = useMemo(() => {
    if (effectiveService !== 'd28d' || !selectedPlan) return [];
    const allowed = Array.isArray(selectedPlan.cycle_ids)
      ? selectedPlan.cycle_ids.map(Number).filter(Number.isFinite)
      : [];
    let pool = allowed.length
      ? cycles.filter((c) => allowed.includes(Number(c.id)))
      : cycles;
    // Remap por fecha si el plan trae ids de otro ámbito (no mostrar todo el catálogo).
    if (allowed.length && !pool.length) {
      const startKeys = new Set(
        allowed
          .map((id) => {
            const raw = cycles.find((c) => Number(c.id) === Number(id));
            return String(raw?.startDate || raw?.start_date || '').slice(0, 10);
          })
          .filter(Boolean),
      );
      pool = startKeys.size
        ? cycles.filter((c) => startKeys.has(String(c.startDate || c.start_date || '').slice(0, 10)))
        : [];
    }
    return filterPurchasableCycles(pool)
      .sort((a, b) => String(a.startDate || a.start_date || '').localeCompare(String(b.startDate || b.start_date || '')));
  }, [cycles, selectedPlan, effectiveService]);

  const planCyclesCount = planCyclesIncluded(selectedPlan);
  const selectedCyclePack = useMemo(() => {
    if (!selectedCycleId || planCyclesCount <= 1) return [];
    return consecutiveCyclePack(cyclesForSelectedPlan, selectedCycleId, planCyclesCount);
  }, [cyclesForSelectedPlan, selectedCycleId, planCyclesCount]);

  const cycleOptionLabel = (c) => {
    if (!c) return '';
    const start = formatCycleDate(c.startDate || c.start_date);
    const end = formatCycleDate(c.endDate || c.end_date);
    const range = start && end ? ` (${start} – ${end})` : (start ? ` (${start})` : '');
    return `${c.name || `Ciclo ${c.id}`}${range}`;
  };

  useEffect(() => {
    if (effectiveService !== 'd28d') {
      setCycles([]);
      setSelectedCycleId('');
      return undefined;
    }
    let cancelled = false;
    const params = {};
    if (inviteContext?.gym_id) {
      params.gym_id = inviteContext.gym_id;
      params.gym_only = 'true';
    } else {
      params.platform_only = 'true';
    }
    api.get('/cycles/public', { params, skipShellAuth: true })
      .then((res) => {
        if (cancelled) return;
        setCycles(res.data?.data || res.data || []);
      })
      .catch(() => {
        if (!cancelled) setCycles([]);
      });
    return () => { cancelled = true; };
  }, [effectiveService, inviteContext?.gym_id]);

  useEffect(() => {
    setSelectedCycleId('');
  }, [selectedPlan?.nombre]);

  useEffect(() => {
    if (!cyclesForSelectedPlan.length) return;
    if (selectedCycleId && cyclesForSelectedPlan.some((c) => String(c.id) === String(selectedCycleId))) return;
    // Preferir ciclo operativo (vigente hoy); si no, el próximo; si no, el primero.
    const preferred = pickDefaultCycleId(cyclesForSelectedPlan);
    setSelectedCycleId(preferred || String(cyclesForSelectedPlan[0].id));
  }, [cyclesForSelectedPlan, selectedCycleId]);

  useEffect(() => {
    if (selectedPlan || !filteredPlans.length) return;
    const allow = Array.isArray(inviteContext?.available_plan_ids) ? inviteContext.available_plan_ids : [];
    const preferredName = inviteContext?.suggested_plan
      || (allow.length === 1 ? allow[0] : null);
    if (preferredName) {
      const suggested = filteredPlans.find((p) => planMatchesInviteAllowList(p, [preferredName]))
        || filteredPlans.find((p) => String(p.nombre) === String(preferredName));
      if (suggested) {
        setSelectedPlan(suggested);
        return;
      }
    }
    if ((inviteContext?.type === 'gym' || inviteContext?.is_sede) && filteredPlans.length === 1) {
      setSelectedPlan(filteredPlans[0]);
    }
  }, [inviteContext, filteredPlans, selectedPlan]);

  useEffect(() => {
    if (!landingPreset || landingPresetApplied.current || !filteredPlans.length) return;
    const needle = String(landingPreset.plan || 'basico')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const programKey = String(landingPreset.programId || '').toLowerCase();
    const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const byProgram = programKey
      ? filteredPlans.filter((p) => String(p.program_id || '').toLowerCase() === programKey)
      : filteredPlans;
    const pool = byProgram.length ? byProgram : filteredPlans;
    const wantBasic = needle === 'basico' || needle === 'basic';
    const match = pool.find((p) => {
      const n = norm(p.nombre || p.name);
      if (wantBasic) return n.includes('basico') || n.includes('basic');
      return n.includes(needle) || String(p.id) === needle;
    }) || pool[0];
    if (!match) return;
    landingPresetApplied.current = true;
    setSelectedPlan(match);
    setStep(5);
  }, [landingPreset, filteredPlans]);

  useEffect(() => {
    if (step !== 3 || !filteredPlans.length) {
      setShowPlanModal(false);
      return;
    }
    // Solo abrir picker si aún no hay plan por defecto (varios planes gym).
    if (inviteContext?.type === 'gym' && !selectedPlan && !inviteContext?.suggested_plan) {
      setShowPlanModal(true);
    }
  }, [step, filteredPlans.length, inviteContext?.type, inviteContext?.suggested_plan, selectedPlan]);

  useEffect(() => {
    const svc = effectiveService;
    if (!svc) return;
    // Registro gratis D28D: cargar catálogo plataforma aunque aún no haya programa.
    if (svc === 'd28d' && !effectiveProgramId && !inviteContext?.gym_id && inviteContext) return;
    const isGymInvite = inviteContext?.type === 'gym' && inviteContext.gym_id;
    let cancelled = false;

    const loadPlans = async () => {
      try {
        setUsingGlobalPlanFallback(false);
        if (isGymInvite) {
          const isSedeInvite = Boolean(inviteContext?.is_sede || inviteContext?.parent_gym_id);
          const gymRes = await api.get('/accounts/plans', {
            params: { visible: 'true', kind: svc, gym_id: inviteContext.gym_id },
            skipShellAuth: true,
          });
          const gymPlans = readPlanList(gymRes.data);
          if (cancelled) return;
          if (gymPlans.length) {
            setPlans(gymPlans);
            return;
          }
          // Sede: nunca caer al catálogo plataforma (Pancitas/Vital/Virtual).
          if (isSedeInvite) {
            setUsingGlobalPlanFallback(false);
            setPlans([]);
            return;
          }
          const globalRes = await api.get('/accounts/plans', {
            params: { visible: 'true', kind: svc, platform_only: 'true' },
            skipShellAuth: true,
          });
          if (cancelled) return;
          setUsingGlobalPlanFallback(true);
          setPlans(readPlanList(globalRes.data));
          return;
        }

        const params = { visible: 'true', kind: svc };
        if (svc === 'training') {
          // Cliente Training (cualquier coach) → planes audience=client.
          // Alta coach marca blanca (inviteServiceChoice=training) → audience=coach.
          if (inviteContext?.type === 'trainer' && inviteServiceChoice === 'training_client') {
            params.training_audience = 'client';
          } else if (inviteContext?.type === 'trainer' && inviteServiceChoice === 'training') {
            params.training_audience = 'coach';
          } else if (!inviteContext || inviteServiceChoice === 'training_client') {
            params.training_audience = 'client';
          }
          if (!isGymInvite) params.platform_only = 'true';
        }
        // Registro gratis: todos los planes D28D de plataforma (sin filtrar por programa).
        if (svc === 'd28d' && !inviteContext) {
          params.platform_only = 'true';
        } else if (svc === 'food' && !inviteContext) {
          params.platform_only = 'true';
        } else if (svc === 'd28d' && effectiveProgramId) {
          params.program_id = effectiveProgramId;
        }
        const res = await api.get('/accounts/plans', { params, skipShellAuth: true });
        if (cancelled) return;
        setPlans(readPlanList(res.data));
      } catch (err) {
        if (cancelled) return;
        console.error('Error cargando planes:', err);
        setUsingGlobalPlanFallback(false);
        setPlans([]);
      }
    };

    loadPlans();

    return () => {
      cancelled = true;
    };
  }, [effectiveService, effectiveProgramId, inviteContext?.type, inviteContext?.gym_id, inviteContext?.is_nicolas_anchor, inviteContext, inviteServiceChoice, step]);

  // Al llegar al paso de plan, revalidar invitación para refrescar available_plan_ids.
  useEffect(() => {
    if (step !== 3 || skipMemberPayment || !inviteContext?.gym_id || !inviteCode.trim()) return;
    let cancelled = false;
    api.post('/auth/resolve-invite', { code: inviteCode.trim() }, { skipShellAuth: true })
      .then((res) => {
        if (cancelled) return;
        const ctx = res.data?.data;
        if (ctx?.type === 'gym') setInviteContext(ctx);
      })
      .catch(() => { /* keep current context */ });
    return () => { cancelled = true; };
  }, [step, skipMemberPayment, inviteContext?.gym_id, inviteCode]);

  const priceForCurrency = (plan, moneda) => {
    if (!plan) return '';
    if (moneda === 'USD') {
      const usd = Number(plan.precio_mensual_usd || 0);
      return usd > 0 ? `USD ${usd.toLocaleString('en-US')} / mes` : 'Precio en USD no configurado';
    }
    return `$${Number(plan.precio_mensual || 0).toLocaleString('es-CO')} COP / mes`;
  };

  const validateInviteCode = async (e) => {
    e?.preventDefault?.();
    setError('');
    setFieldErrors({});
    if (!inviteCode.trim()) {
      reportError('Ingresa tu código de invitación', 'inviteCode');
      return;
    }
    setValidatingCode(true);
    try {
      const res = await api.post('/auth/resolve-invite', { code: inviteCode.trim() }, { skipShellAuth: true });
      const ctx = res.data?.data;
      if (!ctx) {
        reportError('Código no válido', 'inviteCode');
        return;
      }
      setInviteContext(ctx);
      setInviteServiceChoice('');
      setSelectedPlan(null);
      if (ctx.program_id) setProgramId(ctx.program_id);
      setStep(stepAfterInvite(ctx));
    } catch (err) {
      reportError(readApiError(err, 'No pudimos validar el código'), 'inviteCode');
    } finally {
      setValidatingCode(false);
    }
  };

  const goNext = () => {
    setError('');
    setFieldErrors({});
    if (step === 1 && !service && entryMode === 'direct') {
      reportError('Selecciona un servicio', 'service');
      return;
    }
    if (step === 2 && isTrainerInvite) {
      if (!inviteServiceChoice) {
        reportError('Selecciona FOOD PLAN o Entrenadores', 'service');
        return;
      }
      setSelectedPlan(null);
      if (inviteServiceChoice === 'training') {
        setFormData((prev) => ({ ...prev, metodoPago: 'pago_pendiente' }));
        setStep(5);
        return;
      }
      if (inviteServiceChoice === 'training_client') {
        setFormData((prev) => ({ ...prev, metodoPago: 'pago_pendiente' }));
        setStep(5);
        return;
      }
      setStep(3);
      return;
    }
    if (step === 2 && skipMemberPayment && inviteContext) {
      const ids = Array.isArray(inviteContext.program_ids) && inviteContext.program_ids.length
        ? inviteContext.program_ids
        : (inviteContext.program_id ? [inviteContext.program_id] : []);
      if (ids.length > 1 && !effectiveProgramId) {
        reportError('Selecciona un programa', 'program');
        return;
      }
      setStep(5);
      return;
    }
    if (step === 2 && service === 'd28d' && !programId && inviteContext) {
      reportError('Selecciona un programa', 'program');
      return;
    }
    if (step === 3 && skipMemberPayment) {
      if (inviteContext?.is_sede && !selectedPlan) {
        reportError('Selecciona un plan de sede', 'plan');
        return;
      }
      setStep(5);
      return;
    }
    if (step === 3 && !selectedPlan) {
      reportError('Selecciona un plan', 'plan');
      return;
    }
    if (step === 3 && effectiveService === 'd28d' && cyclesForSelectedPlan.length > 0 && !selectedCycleId) {
      reportError('Selecciona el ciclo que estás adquiriendo', 'cycle');
      return;
    }
    if (step === 3 && !currency) {
      reportError('Selecciona si pagarás en COP o USD', 'currency');
      return;
    }
    if (step === 4 && !currency) {
      reportError('Selecciona moneda', 'currency');
      return;
    }
    if (step === 1 && service !== 'd28d') {
      if (service === 'training') {
        setFormData((prev) => ({ ...prev, metodoPago: 'pago_pendiente' }));
      }
      setStep(3);
      return;
    }
    setStep((s) => s + 1);
  };

  const needsProfileBody = effectiveService === 'food' || effectiveService === 'training';

  const redirectAfterRegister = (planRes) => {
    const sedePending = planRes?.data?.sede_member_pending_activation === true
      || (skipMemberPayment && planRes?.data?.pending !== false);
    const pending = planRes?.data?.pending === true
      || (planRes?.data?.pending !== false && (
        skipMemberPayment
        || formData.metodoPago === 'pago_pendiente'
        || formData.metodoPago === 'pago_sede'
        || formData.metodoPago === 'llave'
        || isTrainingPendingActivation
      ));
    const serviceLabel = effectiveService === 'food' ? 'FOOD PLAN' : effectiveService === 'training' ? 'Entrenamiento' : 'Fit Live · Programa';

    // Wompi siempre llega con pending:true (activación diferida). Abrir checkout
    // ANTES del redirect a pending_payment; si no, el pago nunca se abre (Mi Cuenta sí lo hace).
    if (formData.metodoPago === 'wompi_online' && planRes?.data?.payment_url) {
      try {
        sessionStorage.setItem('registration_flash', JSON.stringify({
          type: 'info',
          title: 'Registro exitoso',
          message: `Cuenta creada. Completa el pago en línea para activar ${serviceLabel}.`,
        }));
        sessionStorage.setItem('post_payment_boot', effectiveService);
      } catch { /* noop */ }
      openWompiCheckout(planRes.data.payment_url);
      return;
    }
    if (pending || sedePending) {
      const msg = (skipMemberPayment || sedePending)
        ? `¡Registro completado! Tu cuenta ${serviceLabel} quedó pendiente de activación. El administrador de tu sede debe habilitar tu acceso.`
        : isTrainingPendingActivation
        ? `¡Registro completado! Tu cuenta ${serviceLabel} quedó pendiente de activación. Tu entrenador pagará vía Wompi desde su panel o un administrador activará tu acceso.`
        : formData.metodoPago === 'llave'
          ? `¡Registro completado! Tu plan ${serviceLabel} quedó pendiente. Paga por llave al 3192635819. Un administrador activa tu acceso cuando confirme el pago.`
          : formData.metodoPago === 'pago_sede'
          ? `¡Registro completado! Tu plan ${serviceLabel} quedó registrado con pago en sede. Acércate a tu gimnasio o espera que un administrador confirme el pago para activar tu acceso.`
          : `¡Registro completado! Tu plan ${serviceLabel} quedó en estado pendiente. Un administrador activará tu acceso cuando confirme el pago.`;
      try {
        sessionStorage.setItem('registration_flash', JSON.stringify({ type: 'success', title: 'Registro exitoso', message: msg }));
      } catch { /* noop */ }
      setSuccessMsg(msg);
      window.setTimeout(() => {
        window.location.assign(`/?pending_payment=1&service=${effectiveService}`);
      }, 2800);
      return;
    }
    const okMsg = `¡Registro y plan ${serviceLabel} activados correctamente!`;
    try {
      sessionStorage.setItem('registration_flash', JSON.stringify({ type: 'success', title: 'Registro exitoso', message: okMsg }));
    } catch { /* noop */ }
    setSuccessMsg(okMsg);
    window.setTimeout(() => {
      if (effectiveService === 'training') {
        window.location.assign('/?boot=training&tab=entrenamiento');
        return;
      }
      if (effectiveService === 'food') {
        // Ir al shell autenticado; el CTA Food abre SSO. Evita bounce a /login
        // si redirectToFoodLogin no encuentra token a tiempo o falla el embed.
        window.location.assign('/?boot=food');
        return;
      }
      window.location.assign('/?boot=mi-progreso');
    }, 2200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    if (formData.password !== formData.confirmPassword) {
      reportError('Las contraseñas no coinciden', 'confirmPassword');
      return;
    }
    const emailNorm = String(formData.email || '').trim().toLowerCase();
    const emailConfirmNorm = String(formData.emailConfirm || '').trim().toLowerCase();
    if (!emailNorm || !emailConfirmNorm) {
      reportError('Debes escribir y confirmar tu correo electrónico', !emailNorm ? 'email' : 'emailConfirm');
      return;
    }
    if (emailNorm !== emailConfirmNorm) {
      reportError('Los correos electrónicos no coinciden. Revísalos e inténtalo de nuevo.', 'emailConfirm');
      return;
    }
    if (formData.password.length < 8) {
      reportError('La contraseña debe tener al menos 8 caracteres', 'password');
      return;
    }
    if (!formData.genero) {
      reportError('Selecciona tu género', 'genero');
      return;
    }
    if (!acceptPrivacy || !acceptTerms) {
      reportError('Debes aceptar la Política de Privacidad y los Términos y Condiciones', 'policies');
      return;
    }
    if (needsProfileBody) {
      if (!formData.peso || !formData.altura) {
        reportError('Ingresa tu peso y altura (kg / cm)', 'peso');
        return;
      }
      if (!String(formData.teléfono || '').trim()) {
        reportError('Ingresa tu teléfono de contacto', 'teléfono');
        return;
      }
    }
    const allowedMethods = skipMemberPayment
      ? ['registro_sede']
      : skipTrainingClientPlan
        ? ['pago_pendiente', 'registro_sede', ...(paymentMethods.map((m) => m.id))]
        : (paymentMethods.length
          ? paymentMethods.map((m) => m.id)
          : ['wompi_online', 'pago_sede']);
    if ((effectiveService === 'food' || isFoodLandingRegister) && !allowedMethods.includes('llave')) {
      allowedMethods.push('llave');
    }
    const metodoPago = skipMemberPayment ? 'registro_sede' : formData.metodoPago;
    if (!allowedMethods.includes(metodoPago)) {
      reportError('Selecciona un método de pago válido para este servicio', 'metodoPago');
      return;
    }
    const isCouplePlan = selectedPlan?.is_couple === true;
    if (isCouplePlan) {
      const pEmail = String(formData.partner_email || '').trim().toLowerCase();
      const pEmail2 = String(formData.partner_emailConfirm || '').trim().toLowerCase();
      if (!String(formData.partner_nombre || '').trim()) {
        reportError('Ingresa el nombre de tu pareja', 'partner_nombre');
        return;
      }
      if (!pEmail || !pEmail2) {
        reportError('Escribe y confirma el correo de tu pareja', !pEmail ? 'partner_email' : 'partner_emailConfirm');
        return;
      }
      if (pEmail !== pEmail2) {
        reportError('Los correos de tu pareja no coinciden', 'partner_emailConfirm');
        return;
      }
      if (pEmail === emailNorm) {
        reportError('El correo de tu pareja debe ser distinto al tuyo', 'partner_email');
        return;
      }
      if (!formData.partner_password || formData.partner_password.length < 8) {
        reportError('La contraseña de tu pareja debe tener al menos 8 caracteres', 'partner_password');
        return;
      }
      if (formData.partner_password !== formData.partner_confirmPassword) {
        reportError('Las contraseñas de tu pareja no coinciden', 'partner_confirmPassword');
        return;
      }
    }
    const needsPaidPlan = !skipMemberPayment && !skipTrainingClientPlan;
    if (needsPaidPlan && !String(selectedPlan?.nombre || '').trim()) {
      reportError('Espera a que cargue el plan o vuelve atrás y selecciónalo', 'plan');
      return;
    }
    if (needsPaidPlan && effectiveService === 'd28d' && cyclesForSelectedPlan.length > 0 && !selectedCycleId) {
      reportError('Selecciona el ciclo que estás adquiriendo', 'cycle');
      return;
    }
    setLoading(true);
    try {
      const module_access = isTrainerInvite
        ? (inviteServiceChoice === 'training_client'
          ? { training: true }
          : { ...MODULE_PRESETS[effectiveService] })
        : {
          ...(inviteContext?.module_access || MODULE_PRESETS[effectiveService]),
          ...(effectiveService === 'd28d' && (selectedPlan?.program_id || effectiveProgramId)
            ? { d28d_program: selectedPlan?.program_id || effectiveProgramId }
            : {}),
        };

      // D28D plataforma/gym: validar plan↔ciclo ANTES de crear el usuario (evita huérfanos + correo).
      if (effectiveService === 'd28d' && selectedPlan?.nombre && !skipMemberPayment && !skipTrainingClientPlan) {
        if (!selectedCycleId) {
          reportError('Selecciona un ciclo para continuar');
          setLoading(false);
          return;
        }
        try {
          const checkParams = {
            plan: selectedPlan.nombre,
            cycle_id: Number(selectedCycleId),
          };
          if (inviteContext?.gym_id != null) checkParams.gym_id = inviteContext.gym_id;
          const checkRes = await api.get('/cycles/public/enrollment-check', {
            params: checkParams,
            skipShellAuth: true,
          });
          if (!checkRes.data?.ok) {
            reportError(checkRes.data?.error || 'El ciclo seleccionado no está permitido para este plan');
            setLoading(false);
            return;
          }
        } catch (checkErr) {
          const checkMsg = readApiError(checkErr, 'El ciclo seleccionado no está permitido para este plan');
          reportError(checkMsg);
          setLoading(false);
          return;
        }
      }

      let resumedIncomplete = false;
      try {
        await register({
          nombre: formData.nombre,
          email: String(formData.email).trim().toLowerCase(),
          password: formData.password,
          teléfono: formData.teléfono,
          genero: formData.genero,
          peso: formData.peso ? Number(formData.peso) : null,
          altura: formData.altura ? Number(formData.altura) : null,
          fecha_nacimiento: formData.fecha_nacimiento || null,
          objetivo: formData.objetivo || null,
          tiene_restricciones: Boolean(formData.tiene_restricciones),
          restricciones_detalles: formData.restricciones_detalles || '',
          soy_mama_posparto: Boolean(formData.soy_mama_posparto),
          module_access,
          // Training coach: nunca enviar gym_id (evita cruce sede/D28D).
          gym_id: isTrainerInvite
            ? null
            : (inviteContext?.gym_id ?? (effectiveService === 'd28d' ? undefined : null)),
          trainer_id: inviteContext?.trainer_id ?? null,
          invite_code: inviteContext ? inviteCode.trim() : undefined,
          promo_code: isJabelTrainingPromoReg ? String(formData.promo_code || '').trim() : undefined,
        });
      } catch (regErr) {
        // Huérfano previo (usuario creado, plan falló): reanudar con login + POST /accounts.
        const status = regErr?.response?.status;
        const dup = status === 409 || /ya existe.*correo/i.test(String(regErr?.response?.data?.error || ''));
        if (!(isTrainingCoachClientPending && dup)) throw regErr;
        resumedIncomplete = true;
      }
      // Login solo guarda token en storage (sin setUser) para que App no monte
      // Dashboard y desmonte el wizard antes de crear la cuenta / plan.
      const emailNormLogin = String(formData.email).trim().toLowerCase();
      const loginRes = await authService.login(emailNormLogin, formData.password);
      const newToken = loginRes.data?.token;
      if (!newToken) {
        throw new Error('No se pudo iniciar sesión tras el registro');
      }
      setShellToken(newToken);

      if (resumedIncomplete) {
        const meRes = await api.get('/accounts/me');
        const hasAccount = Boolean(meRes?.data?.hasAccount);
        const st = String(meRes?.data?.account?.estado || '').toLowerCase();
        const pendingOk = new Set([
          'pendiente_pago_online',
          'pendiente_sede',
          'pendiente',
          'pendiente_activacion',
          'pendiente_habilitacion',
        ]);
        if (hasAccount && !pendingOk.has(st)) {
          throw new Error(
            'Ya existe una cuenta activa con este correo. Inicia sesión o recupera tu contraseña.',
          );
        }
      }

      if (skipTrainingClientPlan) {
        redirectAfterRegister({ data: { pending: true } });
        return;
      }

      // Plan pareja: redimir asiento del titular (cuenta independiente amarrada a plan/ciclo).
      if (inviteContext?.type === 'couple') {
        const coupleCode = inviteContext.couple_code || inviteCode.trim();
        const redeemRes = await api.post('/accounts/couple/redeem', { couple_code: coupleCode });
        redirectAfterRegister(redeemRes);
        return;
      }

      const moduleCode = effectiveService === 'food' ? 'food' : effectiveService === 'training' ? 'training' : 'd28d';
      // Sede (skip_member_payment): el wizard no pide ciclo; el backend asigna el operativo de la sede.
      // No enviar plan.cycle_ids[0] (puede estar vencido → "ciclo seleccionado no disponible" tras crear user).
      const resolvedCycleId = skipMemberPayment || effectiveService !== 'd28d'
        ? null
        : (selectedCycleId
          ? Number(selectedCycleId)
          : null);
      const accountPayload = selectedPlan?.nombre
        ? {
          plan: selectedPlan.nombre,
          gym_id: isTrainerInvite ? undefined : (inviteContext?.gym_id ?? undefined),
          cycle_id: Number.isFinite(resolvedCycleId) ? resolvedCycleId : undefined,
          metodoPago,
          module_code: moduleCode,
          currency,
          provision_password: formData.password,
          ...(isCouplePlan ? {
            partner: {
              nombre: String(formData.partner_nombre || '').trim(),
              email: String(formData.partner_email || '').trim().toLowerCase(),
              password: formData.partner_password,
              teléfono: formData.partner_teléfono || null,
              genero: formData.partner_genero || null,
            },
          } : {}),
        }
        : {
          metodoPago,
          module_code: moduleCode,
          program_id: effectiveProgramId || undefined,
          currency: 'COP',
          provision_password: formData.password,
        };
      const planRes = await api.post('/accounts', accountPayload);
      redirectAfterRegister(planRes);
    } catch (err) {
      // Si el usuario ya se creó pero falló el plan, desactivar para no dejar huérfano
      // y permitir reintento con el mismo email.
      try {
        const tok = getShellToken();
        if (tok) {
          await api.post('/auth/rollback-incomplete-registration');
        }
      } catch { /* best-effort */ }
      try { clearShellToken(); } catch { /* noop */ }
      const rawMsg = readApiError(err, 'No se pudo completar el registro');
      const deactivated = err?.response?.data?.code === 'account_deactivated'
        || /desactivad/i.test(String(rawMsg));
      const msg = deactivated
        ? 'No se pudo reactivar la cuenta. Completa todos los pasos del registro (producto FOOD PLAN + datos) o contacta soporte.'
        : rawMsg;
      reportError(msg);
      try {
        sessionStorage.setItem('registration_flash', JSON.stringify({
          type: 'error',
          title: 'No se pudo completar',
          message: msg,
        }));
      } catch { /* noop */ }
    } finally {
      setLoading(false);
    }
  };

  const maxStep = 5;
  const titles = isTrainerInvite
    ? ['Código', 'Servicio', 'Plan', 'Moneda', 'Datos y pago']
    : ['Servicio', 'Programa', 'Plan', 'Moneda', 'Datos y pago'];

  const trainerInviteSlug = inviteContext?.type === 'trainer'
    ? String(inviteContext.brand_slug || '').trim()
      || slugifyBrandClient(inviteContext.brand_name || String(inviteContext.label || '').replace(/^Entrenador:\s*/i, ''))
    : '';
  const registerBrandLabel = isFoodLandingRegister
    ? 'Food Plan'
    : (trainerInviteSlug
      ? (inviteContext?.brand_name || String(inviteContext?.label || '').replace(/^Entrenador:\s*/i, '') || 'Entrenamiento')
      : (isTrainingOrgRegister ? (inviteContext?.label || 'Entrenamiento') : displayBrand));

  return (
    <>
    <AuthLayout
      wide
      variant={(isFoodLandingRegister || isTrainingOrgRegister) ? (isFoodLandingRegister ? 'food' : 'd28d') : 'd28d'}
      trainerSlug={trainerInviteSlug}
      title={`Registro — ${titles[step - 1] || ''}`}
      subtitle={`Paso ${step} de ${maxStep} · ${registerBrandLabel}`}
    >
      {reactivateMode ? (
        <div className="rounded-lg border border-lime-500/40 bg-lime-950/30 text-lime-100 text-sm p-3 mb-2">
          Estás <strong>reactivando</strong> una cuenta desactivada. Elige el producto (p. ej. <strong>FOOD PLAN</strong>),
          completa los pasos y define una contraseña nueva para Ecosistema Fit. Luego podrás entrar por «Iniciar sesión».
        </div>
      ) : null}
      {error && <div className="error-message">{error}</div>}
      {successMsg && <div className="success-message">{successMsg}</div>}

      {step === 1 && entryMode === 'invite' && validatingCode && !inviteContext && (
        <p className="text-sm text-stone-300 text-center py-6">Validando código de invitación…</p>
      )}

      {step === 1 && entryMode === 'invite' && !(validatingCode && !inviteContext) && (
        <form onSubmit={validateInviteCode} className="space-y-3">
          <p className="text-sm text-stone-400">
            Código de programa, entrenador, gimnasio o pareja.
          </p>
          <input
            className="input font-mono uppercase"
            placeholder="Ej. GYM-PRO-001"
            data-field="inviteCode"
            value={inviteCode}
            onChange={(e) => {
              clearFieldError('inviteCode');
              setInviteCode(e.target.value.toUpperCase());
            }}
            required
          />
          <FieldNote message={fieldErrors.inviteCode} />
          <button type="submit" className="btn-primary w-full" disabled={validatingCode}>
            {validatingCode ? 'Validando…' : 'Validar código'}
          </button>
          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => { setEntryMode('direct'); setInviteCode(''); setInviteContext(null); setError(''); }}
          >
            Volver a registro directo
          </button>
        </form>
      )}

      {step === 1 && entryMode === 'direct' && isTrainingOrgRegister && (
        <div className="space-y-3">
          <p className="text-sm text-center py-6">
            {error || 'Preparando tu registro con tu entrenador…'}
          </p>
        </div>
      )}

      {step === 1 && entryMode === 'direct' && isFoodLandingRegister && (
        <div className="space-y-3">
          <p className="text-sm text-center py-6">
            {selectedPlan ? 'Tu plan Food Plan está listo.' : 'Preparando tu registro Food Plan…'}
          </p>
          <button
            type="button"
            className="btn-primary w-full"
            disabled={!selectedPlan}
            onClick={() => setStep(5)}
          >
            Continuar
          </button>
        </div>
      )}

      {step === 1 && entryMode === 'direct' && !isFoodLandingRegister && !isTrainingOrgRegister && (
        <div className="space-y-3" data-field="service">
          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => { setEntryMode('invite'); setError(''); setFieldErrors({}); }}
          >
            Tengo código de invitación (gym, coach o programa)
          </button>
          <p className="text-center text-xs text-stone-500">— o regístrate directo en la plataforma —</p>
          <p className="text-sm text-stone-400">
            Sin código, el registro directo usa el gimnasio virtual de la plataforma.
            Si eliges <strong className="text-stone-200">Entrenadores</strong>, tu coach por defecto es Nicolas del Rio (producto Training, separado del gym virtual).
          </p>
          <FieldNote message={fieldErrors.service} />
          {SERVICES.map((s) => {
            const selected = service === s.id;
            return (
            <button
              key={s.id}
              type="button"
              aria-pressed={selected}
              className={`register-option w-full text-left p-4 rounded-xl relative ${
                selected ? 'register-option--active' : ''
              }`}
              onClick={() => {
                clearFieldError('service');
                // FOOD PLAN se registra en el ecosistema (embed /food); no redirigir a /login.
                setService(s.id);
                setProgramId('');
                setSelectedPlan(null);
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold register-option-title text-lg">{s.label}</p>
                  <p className="text-sm register-option-muted mt-1">{s.desc}</p>
                </div>
                {selected ? (
                  <span className="register-option-selected-badge shrink-0 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide">
                    <CheckCircle2 className="w-4 h-4" aria-hidden />
                    Seleccionado
                  </span>
                ) : (
                  <span className="register-option-unselected-hint shrink-0 text-xs register-option-muted">
                    Elegir
                  </span>
                )}
              </div>
            </button>
            );
          })}
          <button type="button" className="btn-primary w-full" onClick={goNext}>Continuar</button>
        </div>
      )}

      {step === 2 && isTrainerInvite && (
        <div className="space-y-3">
          <p className="text-sm text-stone-400">
            Con el código de tu entrenador puedes registrarte para entrenamiento (tu coach activa el acceso)
            o contratar FOOD PLAN.
          </p>
          {inviteTrainerServices.map((s) => {
            const selected = inviteServiceChoice === s.id;
            return (
              <button
                key={s.id}
                type="button"
                aria-pressed={selected}
                className={`register-option w-full text-left p-4 rounded-xl border-2 relative ${
                  selected ? 'register-option--active' : ''
                }`}
                onClick={() => {
                  // FOOD PLAN vía wizard del ecosistema (no bounce a /login sin sesión).
                  setInviteServiceChoice(s.id);
                  setSelectedPlan(null);
                  setCurrency('COP');
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold register-option-title text-lg">{s.label}</p>
                    <p className="text-sm register-option-muted mt-1">{s.desc}</p>
                  </div>
                  {selected ? (
                    <span className="register-option-selected-badge shrink-0 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide">
                      <CheckCircle2 className="w-5 h-5" aria-hidden />
                      Seleccionado
                    </span>
                  ) : (
                    <span className="register-option-unselected-hint shrink-0 text-xs register-option-muted">
                      Elegir
                    </span>
                  )}
                </div>
              </button>
            );
          })}
          <div className="flex gap-2">
            <button type="button" className="btn-secondary flex-1" onClick={() => { setStep(1); setInviteContext(null); setInviteServiceChoice(''); }}>Atrás</button>
            <button type="button" className="btn-primary flex-1" onClick={goNext} disabled={!inviteServiceChoice}>Continuar</button>
          </div>
        </div>
      )}

      {step === 2 && !isTrainerInvite && (
        <div className="space-y-3">
          {!inviteContext && service === 'd28d' ? (
            <button
              type="button"
              aria-pressed={!programId}
              className={`register-option w-full text-left p-4 rounded-xl relative ${
                !programId ? 'register-option--active' : ''
              }`}
              onClick={() => {
                setProgramId('');
                setSelectedPlan(null);
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold register-option-title">Todos los programas</p>
                  <p className="text-xs register-option-muted mt-1">Ver todos los planes de Fit Live · Programa disponibles</p>
                </div>
                {!programId ? (
                  <span className="register-option-selected-badge shrink-0 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide">
                    <CheckCircle2 className="w-4 h-4" aria-hidden />
                    Seleccionado
                  </span>
                ) : (
                  <span className="register-option-unselected-hint shrink-0 text-xs register-option-muted">
                    Elegir
                  </span>
                )}
              </div>
            </button>
          ) : null}
          {(skipMemberPayment && inviteContext ? invitePrograms : programs.filter((p) => p.active !== false)).map((p) => {
            const selected = String(effectiveProgramId) === String(p.id);
            return (
            <button
              key={p.id}
              type="button"
              aria-pressed={selected}
              className={`register-option w-full text-left p-4 rounded-xl relative ${
                selected ? 'register-option--active' : ''
              }`}
              onClick={() => {
                setProgramId(p.id);
                setSelectedPlan(null);
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold register-option-title">{p.name}</p>
                {selected ? (
                  <span className="register-option-selected-badge shrink-0 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide">
                    <CheckCircle2 className="w-4 h-4" aria-hidden />
                    Seleccionado
                  </span>
                ) : (
                  <span className="register-option-unselected-hint shrink-0 text-xs register-option-muted">
                    Elegir
                  </span>
                )}
              </div>
            </button>
            );
          })}
          {skipMemberPayment && inviteContext && !invitePrograms.length ? (
            <p className="text-sm text-amber-300 rounded-lg border border-amber-500/40 bg-amber-950/30 p-3">
              No hay programas configurados para esta sede. Contacta al administrador de tu gimnasio.
            </p>
          ) : null}
          {!skipMemberPayment && service === 'd28d' && !programs.filter((p) => p.active !== false).length ? (
            <p className="text-stone-600 text-sm">No hay programas disponibles. Contacta a soporte.</p>
          ) : null}
          {!skipMemberPayment && service !== 'd28d' ? (
            <p className="text-stone-600 text-sm">Este servicio no requiere elegir programa. Continúa al plan.</p>
          ) : null}
          <div className="flex gap-2">
            <button type="button" className="btn-secondary flex-1" onClick={() => goWizardBack(1)}>Atrás</button>
            <button type="button" className="btn-primary flex-1" onClick={goNext}>Continuar</button>
          </div>
        </div>
      )}

      {inviteContext && step >= 3 && (
        <p className="text-sm text-lime-400 mb-2">Código: {inviteContext.label || inviteCode}</p>
      )}

      {step === 3 && (!skipMemberPayment || inviteContext?.is_sede) && (
        <div className="space-y-4 notranslate" translate="no">
          <div className="rounded-xl border border-stone-600 bg-stone-900/50 p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-stone-200">
                  {inviteContext?.is_sede ? 'Plan de sede' : 'Plan de suscripción'}
                </p>
                <p className="text-xs register-option-muted mt-1">
                  {inviteContext?.is_sede
                    ? (filteredPlans.length
                      ? `${filteredPlans.length} plan${filteredPlans.length === 1 ? '' : 'es'} disponible${filteredPlans.length === 1 ? '' : 's'} para sedes`
                      : 'Sin planes de sede activos. Contacta a tu gimnasio.')
                    : (filteredPlans.length
                      ? `${filteredPlans.length} plan${filteredPlans.length === 1 ? '' : 'es'} disponible${filteredPlans.length === 1 ? '' : 's'}${inviteContext ? ' para tu gimnasio' : ''}`
                      : (inviteContext ? 'Sin planes activos para este gimnasio' : 'Sin planes activos'))}
                </p>
              </div>
              {filteredPlans.length > 0 ? (
                <button
                  type="button"
                  className="btn-primary text-sm shrink-0"
                  onClick={() => setShowPlanModal(true)}
                >
                  {selectedPlan ? 'Cambiar plan' : 'Ver planes'}
                </button>
              ) : null}
            </div>

            {selectedPlan ? (
              <div className="register-option register-option--active p-4 rounded-xl border">
                <p className="font-semibold register-option-title">{selectedPlan.nombre}</p>
                {selectedPlan.descripcion ? (
                  <p className="text-sm register-option-muted mt-1">{selectedPlan.descripcion}</p>
                ) : null}
                <p className="register-option-price text-sm mt-2">
                  {priceForCurrency(selectedPlan, 'COP')}
                  {Number(selectedPlan.precio_mensual_usd || 0) > 0
                    ? ` · ${priceForCurrency(selectedPlan, 'USD')}`
                    : ''}
                </p>
                {selectedPlan.is_couple ? (
                  <p className="text-xs text-amber-400 mt-1">Plan de pareja (2 usuarios)</p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm register-option-muted">
                {filteredPlans.length
                  ? 'Abre el listado de planes para elegir el que mejor se adapte a ti.'
                  : (isTrainerInvite
                    ? 'No hay planes activos para este servicio. Contacta a soporte o a tu entrenador.'
                    : 'No hay planes activos para este programa. Contacta a tu gimnasio o a soporte.')}
              </p>
            )}
          </div>

          {selectedPlan && (
            <div className="space-y-2 border-t border-stone-600 pt-4">
              <p className="text-sm font-semibold text-stone-200">¿En qué moneda deseas pagar?</p>
              {[
                { id: 'COP', title: 'Pesos colombianos (COP)' },
                { id: 'USD', title: 'Dólares estadounidenses (USD)' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`register-option w-full p-4 rounded-xl border text-left ${
                    currency === opt.id ? 'register-option--active' : ''
                  }`}
                  onClick={() => setCurrency(opt.id)}
                >
                  <span className="font-semibold register-option-title block">{opt.title}</span>
                  <span className="text-sm register-option-muted block mt-1">
                    {priceForCurrency(selectedPlan, opt.id)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {selectedPlan && effectiveService === 'd28d' && cyclesForSelectedPlan.length > 0 && (
            <div className="space-y-2 border-t border-stone-600 pt-4">
              <label className="block space-y-2">
                <span className="text-sm font-semibold text-stone-200">
                  {planCyclesCount > 1
                    ? `Ciclo de inicio (incluye ${planCyclesCount} ciclos consecutivos)`
                    : 'Ciclo que estás adquiriendo'}
                </span>
                <select
                  className="input w-full"
                  value={selectedCycleId}
                  onChange={(e) => setSelectedCycleId(e.target.value)}
                  required
                >
                  <option value="">Selecciona un ciclo</option>
                  {cyclesForSelectedPlan.map((c) => (
                    <option key={c.id} value={c.id}>{cycleOptionLabel(c)}</option>
                  ))}
                </select>
              </label>
              {planCyclesCount > 1 && selectedCyclePack.length > 0 ? (
                <div className="rounded-lg border border-stone-600 bg-stone-900/40 p-3 space-y-1">
                  <p className="text-xs text-stone-300 font-medium">
                    Quedarás inscrito en {selectedCyclePack.length} ciclo{selectedCyclePack.length === 1 ? '' : 's'} consecutivos:
                  </p>
                  <ul className="text-xs register-option-muted list-disc pl-4 space-y-0.5">
                    {selectedCyclePack.map((c) => (
                      <li key={c.id}>{cycleOptionLabel(c)}</li>
                    ))}
                  </ul>
                  {selectedCyclePack.length < planCyclesCount ? (
                    <p className="text-xs text-amber-300 mt-1">
                      Solo hay {selectedCyclePack.length} ciclo(s) disponible(s) desde el elegido (el plan incluye {planCyclesCount}).
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs register-option-muted">
                  Tu acceso se habilitará en el ciclo elegido cuando un administrador active tu cuenta.
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <button type="button" className="btn-secondary flex-1" onClick={() => {
              if (isFoodLandingRegister) {
                goWizardBack(5);
                return;
              }
              if (isTrainerInvite) {
                setStep(2);
                setSelectedPlan(null);
              } else if (inviteContext) {
                setStep(1);
                setEntryMode('invite');
              } else {
                setStep(effectiveService === 'd28d' ? 2 : 1);
              }
            }}>Atrás</button>
            <button
              type="button"
              className="btn-primary flex-1"
              onClick={goNext}
              disabled={
                !selectedPlan
                || !currency
                || (effectiveService === 'd28d' && cyclesForSelectedPlan.length > 0 && !selectedCycleId)
              }
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {step === 4 && !skipMemberPayment && (
        <div className="space-y-3 notranslate" translate="no">
          <p className="text-sm text-stone-300">Confirma moneda y plan</p>
          <p className="register-option-title font-semibold">{selectedPlan?.nombre}</p>
          {effectiveService === 'd28d' && selectedCycleId ? (
            <div className="text-sm text-stone-300 space-y-1">
              <p>
                Ciclo de inicio:{' '}
                {cycleOptionLabel(cyclesForSelectedPlan.find((c) => String(c.id) === String(selectedCycleId)))
                  || selectedCycleId}
              </p>
              {planCyclesCount > 1 && selectedCyclePack.length > 0 ? (
                <p className="text-xs text-stone-400">
                  Paquete: {selectedCyclePack.map((c) => c.name || `Ciclo ${c.id}`).join(' → ')}
                  {' '}({selectedCyclePack.length}/{planCyclesCount})
                </p>
              ) : null}
            </div>
          ) : null}
          <p className="register-option-price">{priceForCurrency(selectedPlan, currency)}</p>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary flex-1" onClick={() => goWizardBack(3)}>Atrás</button>
            <button type="button" className="btn-primary flex-1" onClick={goNext}>Continuar a datos</button>
          </div>
        </div>
      )}

      {step === 5 && (
        <form onSubmit={handleSubmit} className="space-y-3">
          {isNewCoachTrainingReg && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-950/20 p-3 text-sm text-stone-200">
              <p className="font-semibold text-amber-300">Alta como entrenador marca blanca</p>
              <p className="mt-1 text-stone-300">
                Tu perfil de coach quedará
                <strong> pendiente de activación</strong> hasta que un administrador habilite tu acceso.
              </p>
            </div>
          )}
          {skipMemberPayment && (
            <div className="rounded-lg border border-lime-500/30 bg-lime-950/20 p-3 text-sm text-stone-200">
              <p className="font-semibold text-lime-300">Registro en {sedeLabel || 'sede virtual'}</p>
              {assignedProgramLabel ? (
                <p className="mt-1">Programa asignado: <strong>{assignedProgramLabel}</strong></p>
              ) : null}
              {selectedPlan ? (
                <p className="mt-1">Plan de la sede: <strong>{selectedPlan.nombre}</strong></p>
              ) : null}
              <p className="mt-1 text-stone-300">
                No necesitas pagar en línea. Quedarás pendiente de activación hasta que el administrador de la sede habilite tu acceso.
              </p>
            </div>
          )}
          {selectedPlan && (
            <div
              className={`rounded-lg border p-3 text-sm notranslate ${
                isFoodLandingRegister ? 'food-register-plan-card' : 'border-stone-600 bg-stone-900/40'
              }`}
              translate="no"
            >
              <p><strong>Plan:</strong> {selectedPlan.nombre}</p>
              {effectiveService === 'd28d' && selectedCycleId ? (
                <p>
                  <strong>Ciclo:</strong>{' '}
                  {cycleOptionLabel(cyclesForSelectedPlan.find((c) => String(c.id) === String(selectedCycleId)))
                    || selectedCycleId}
                </p>
              ) : null}
              <p><strong>Pago:</strong> {currency === 'USD' ? 'Dólares (USD)' : 'Pesos colombianos (COP)'}</p>
              <p className="text-lime-400">{priceForCurrency(selectedPlan, currency)}</p>
            </div>
          )}
          <input
            className="input"
            placeholder="Nombre completo"
            required
            data-field="nombre"
            value={formData.nombre}
            onChange={(e) => {
              clearFieldError('nombre');
              setFormData({ ...formData, nombre: e.target.value });
            }}
          />
          <FieldNote message={fieldErrors.nombre} />
          <input
            className="input"
            type="email"
            placeholder="Correo electrónico"
            required
            autoComplete="email"
            data-field="email"
            value={formData.email}
            onChange={(e) => {
              clearFieldError('email');
              setFormData({ ...formData, email: e.target.value });
            }}
          />
          <FieldNote message={fieldErrors.email} />
          <input
            className="input"
            type="email"
            placeholder="Confirma tu correo electrónico"
            required
            autoComplete="email"
            data-field="emailConfirm"
            value={formData.emailConfirm}
            onChange={(e) => {
              clearFieldError('emailConfirm');
              setFormData({ ...formData, emailConfirm: e.target.value });
            }}
          />
          <FieldNote message={fieldErrors.emailConfirm} />
          {formData.email && formData.emailConfirm
            && String(formData.email).trim().toLowerCase() !== String(formData.emailConfirm).trim().toLowerCase() ? (
            <p className="text-xs text-amber-300">Los correos no coinciden.</p>
          ) : null}
          <div className="register-password-wrap" data-field="password">
            <input
              className="input w-full"
              type={showPassword ? 'text' : 'password'}
              placeholder="Contraseña (mín. 8)"
              required
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => {
                clearFieldError('password');
                setFormData({ ...formData, password: e.target.value });
              }}
            />
            <button
              type="button"
              className="register-password-toggle"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? <EyeOff className="w-5 h-5" aria-hidden /> : <Eye className="w-5 h-5" aria-hidden />}
            </button>
          </div>
          <FieldNote message={fieldErrors.password} />
          <div className="register-password-wrap" data-field="confirmPassword">
            <input
              className="input w-full"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirmar contraseña"
              required
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={(e) => {
                clearFieldError('confirmPassword');
                setFormData({ ...formData, confirmPassword: e.target.value });
              }}
            />
            <button
              type="button"
              className="register-password-toggle"
              aria-label={showConfirmPassword ? 'Ocultar confirmación' : 'Mostrar confirmación'}
              onClick={() => setShowConfirmPassword((v) => !v)}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" aria-hidden /> : <Eye className="w-5 h-5" aria-hidden />}
            </button>
          </div>
          <FieldNote message={fieldErrors.confirmPassword} />
          {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword ? (
            <p className="text-xs text-amber-300">Las contraseñas no coinciden.</p>
          ) : null}
          <div data-field="teléfono">
            <PhoneField
              label="Teléfono"
              value={formData.teléfono}
              onChange={(v) => {
                clearFieldError('teléfono');
                setFormData({ ...formData, teléfono: v });
              }}
              required={needsProfileBody}
              inputClassName="input"
              placeholder="300 123 4567"
            />
            <FieldNote message={fieldErrors.teléfono} />
          </div>
          {isJabelTrainingPromoReg ? (
            <div data-field="promo_code">
              <label className="block text-xs font-semibold uppercase tracking-wide text-stone-400 mb-1">
                Código promocional
              </label>
              <input
                className="input"
                type="text"
                autoComplete="off"
                placeholder="Si tienes un código Viento Recio, escríbelo"
                value={formData.promo_code || ''}
                onChange={(e) => {
                  clearFieldError('promo_code');
                  setFormData({ ...formData, promo_code: e.target.value });
                }}
              />
              <p className="text-xs text-stone-500 mt-1">Opcional. No reemplaza el código de invitación de tu entrenador.</p>
              <FieldNote message={fieldErrors.promo_code} />
            </div>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="relative" data-field="fecha_nacimiento">
              {/* type=date ignora placeholder en móvil/Chrome; overlay cuando vacío */}
              {!formData.fecha_nacimiento ? (
                <span className="register-date-placeholder" aria-hidden="true">
                  Fecha de nacimiento
                </span>
              ) : null}
              <input
                className={`input w-full register-date-input${!formData.fecha_nacimiento ? ' is-empty' : ''}`}
                type="date"
                placeholder="Fecha de nacimiento"
                aria-label="Fecha de nacimiento"
                value={formData.fecha_nacimiento}
                onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
              />
            </div>
            <div data-field="genero">
              <select
                className="input"
                required
                value={formData.genero}
                onChange={(e) => {
                  clearFieldError('genero');
                  setFormData({ ...formData, genero: e.target.value });
                }}
              >
                <option value="">Género</option>
                <option value="masculino">Masculino</option>
                <option value="femenino">Femenino</option>
              </select>
              <FieldNote message={fieldErrors.genero} />
            </div>
          </div>
          {(needsProfileBody || effectiveService === 'd28d') && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-field="peso">
              <input className="input" type="number" step="0.1" placeholder="Peso (kg)" required={needsProfileBody}
                value={formData.peso}
                onChange={(e) => {
                  clearFieldError('peso');
                  setFormData({ ...formData, peso: e.target.value });
                }} />
              <input className="input" type="number" step="0.1" placeholder="Altura (cm)" required={needsProfileBody}
                value={formData.altura}
                onChange={(e) => {
                  clearFieldError('peso');
                  setFormData({ ...formData, altura: e.target.value });
                }} />
              <div className="sm:col-span-2">
                <FieldNote message={fieldErrors.peso} />
              </div>
            </div>
          )}
          <select className="input" value={formData.objetivo}
            onChange={(e) => setFormData({ ...formData, objetivo: e.target.value })}>
            <option value="">Objetivo nutricional / fitness</option>
            <option value="pérdida_de_grasa">Pérdida de grasa</option>
            <option value="mantenimiento">Mantenimiento</option>
            <option value="ganancia_muscular">Ganancia muscular</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-stone-300">
            <input
              type="checkbox"
              checked={formData.tiene_restricciones}
              onChange={(e) => setFormData({ ...formData, tiene_restricciones: e.target.checked })}
            />
            Tengo restricciones o recomendaciones alimentarias
          </label>
          {formData.tiene_restricciones && (
            <textarea
              className="input"
              rows={3}
              placeholder="Alergias, vegetariano, recomendaciones médicas…"
              value={formData.restricciones_detalles}
              onChange={(e) => setFormData({ ...formData, restricciones_detalles: e.target.value })}
            />
          )}
          <label className="flex items-center gap-2 text-sm text-stone-300">
            <input
              type="checkbox"
              checked={formData.soy_mama_posparto}
              onChange={(e) => setFormData({ ...formData, soy_mama_posparto: e.target.checked })}
            />
            Soy mamá posparto
          </label>
          {selectedPlan?.is_couple ? (
            <div className="space-y-3 rounded-lg border border-amber-500/30 bg-amber-950/20 p-3">
              <div>
                <p className="text-sm font-semibold text-amber-100">Datos de tu pareja</p>
                <p className="text-xs text-stone-400 mt-1">
                  El plan de pareja incluye dos usuarios. Completa los datos de la segunda persona; se creará su cuenta al confirmar el pago.
                </p>
              </div>
              <div data-field="partner_nombre">
                <input
                  className="input"
                  required
                  placeholder="Nombre completo de tu pareja"
                  value={formData.partner_nombre}
                  onChange={(e) => {
                    clearFieldError('partner_nombre');
                    setFormData({ ...formData, partner_nombre: e.target.value });
                  }}
                />
                <FieldNote message={fieldErrors.partner_nombre} />
              </div>
              <div data-field="partner_email">
                <input
                  className="input"
                  type="email"
                  required
                  autoComplete="off"
                  placeholder="Correo de tu pareja"
                  value={formData.partner_email}
                  onChange={(e) => {
                    clearFieldError('partner_email');
                    setFormData({ ...formData, partner_email: e.target.value });
                  }}
                />
                <FieldNote message={fieldErrors.partner_email} />
              </div>
              <div data-field="partner_emailConfirm">
                <input
                  className="input"
                  type="email"
                  required
                  autoComplete="off"
                  placeholder="Confirma el correo de tu pareja"
                  value={formData.partner_emailConfirm}
                  onChange={(e) => {
                    clearFieldError('partner_emailConfirm');
                    setFormData({ ...formData, partner_emailConfirm: e.target.value });
                  }}
                />
                <FieldNote message={fieldErrors.partner_emailConfirm} />
              </div>
              <div className="register-password-wrap" data-field="partner_password">
                <input
                  className="input"
                  type={showPartnerPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  placeholder="Contraseña de tu pareja (mín. 8)"
                  value={formData.partner_password}
                  onChange={(e) => {
                    clearFieldError('partner_password');
                    setFormData({ ...formData, partner_password: e.target.value });
                  }}
                />
                <button
                  type="button"
                  className="register-password-toggle"
                  onClick={() => setShowPartnerPassword((v) => !v)}
                  aria-label={showPartnerPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPartnerPassword ? <EyeOff className="w-5 h-5" aria-hidden /> : <Eye className="w-5 h-5" aria-hidden />}
                </button>
                <FieldNote message={fieldErrors.partner_password} />
              </div>
              <div className="register-password-wrap" data-field="partner_confirmPassword">
                <input
                  className="input"
                  type={showPartnerPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  placeholder="Confirma la contraseña de tu pareja"
                  value={formData.partner_confirmPassword}
                  onChange={(e) => {
                    clearFieldError('partner_confirmPassword');
                    setFormData({ ...formData, partner_confirmPassword: e.target.value });
                  }}
                />
                <FieldNote message={fieldErrors.partner_confirmPassword} />
              </div>
              <div data-field="partner_teléfono">
                <PhoneField
                  label="Teléfono de tu pareja (opcional)"
                  value={formData.partner_teléfono}
                  onChange={(v) => setFormData({ ...formData, partner_teléfono: v })}
                  required={false}
                  inputClassName="input"
                  placeholder="300 123 4567"
                />
              </div>
              <div data-field="partner_genero">
                <select
                  className="input"
                  value={formData.partner_genero}
                  onChange={(e) => setFormData({ ...formData, partner_genero: e.target.value })}
                >
                  <option value="">Género de tu pareja (opcional)</option>
                  <option value="masculino">Masculino</option>
                  <option value="femenino">Femenino</option>
                </select>
              </div>
            </div>
          ) : null}
          <div className="policies-section space-y-2" data-field="policies">
            <label className="flex items-start gap-2 text-sm text-stone-300">
              <input
                type="checkbox"
                checked={acceptPrivacy}
                onChange={(e) => {
                  clearFieldError('policies');
                  setAcceptPrivacy(e.target.checked);
                }}
                className="mt-1"
              />
              <span>
                Acepto la{' '}
                <button type="button" className="policy-link" onClick={() => setShowPrivacyModal(true)}>
                  Política de Privacidad
                </button>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-stone-300">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => {
                  clearFieldError('policies');
                  setAcceptTerms(e.target.checked);
                }}
                className="mt-1"
              />
              <span>
                Acepto los{' '}
                <button type="button" className="policy-link" onClick={() => setShowTermsModal(true)}>
                  Términos y Condiciones
                </button>
              </span>
            </label>
            <FieldNote message={fieldErrors.policies} />
          </div>
          <div className="space-y-3" data-field="metodoPago">
            <p className="text-sm font-semibold text-stone-200">Método de pago</p>
            {skipMemberPayment || skipTrainingClientPlan ? (
              <p className="text-xs text-stone-200 rounded-lg border border-lime-500/30 bg-lime-950/20 p-3">
                {isTrainingCoachClientPending
                  ? 'Tu cuenta de entrenamiento quedará pendiente. Tu coach activará el acceso desde «Activar clientes» (pago Wompi por persona según el plan parametrizado).'
                  : skipCoachTrainingPlan
                    ? 'Tu cuenta de entrenador quedará pendiente hasta activación. Solo completa tus datos para crear tu cuenta.'
                    : `Al registrarte con el código de ${sedeLabel || 'tu sede'} no necesitas pagar en línea. Quedarás pendiente de activación hasta que el administrador de la sede habilite tu acceso.`}
              </p>
            ) : (
            <>
            {isTrainingPendingActivation && !skipTrainingClientPlan ? (
              <p className="text-xs text-stone-300 rounded-lg border border-lime-500/30 bg-lime-950/20 p-3">
                Tu registro de entrenador quedará pendiente hasta activación administrativa.
              </p>
            ) : null}
            {paymentMethodsLoading ? (
              <p className="text-sm text-stone-400">Cargando métodos de pago…</p>
            ) : null}
            {(
              (effectiveService === 'food' || isFoodLandingRegister)
                ? [
                  ...(paymentMethods.length ? paymentMethods : [
                    { id: 'wompi_online', label: 'Pago en línea (Wompi)' },
                    { id: 'pago_sede', label: 'Pago en sede' },
                  ]),
                  { id: 'llave', label: 'Pago por llave' },
                ]
                : (paymentMethods.length ? paymentMethods : [
                  { id: 'wompi_online', label: 'Pago en línea (Wompi)' },
                  { id: 'pago_sede', label: 'Pago en sede' },
                ])
            ).map((m) => (
              <button
                key={m.id}
                type="button"
                className={`register-option w-full text-left p-4 rounded-xl border ${
                  formData.metodoPago === m.id ? 'register-option--active' : ''
                }`}
                onClick={() => {
                  clearFieldError('metodoPago');
                  setFormData({ ...formData, metodoPago: m.id });
                }}
              >
                <span className="font-semibold register-option-title block">{m.label}</span>
                {m.id === 'wompi_online' ? (
                  <span className="text-xs register-option-muted block mt-1">Tarjeta, PSE o Nequi — activación inmediata tras confirmar.</span>
                ) : null}
                {m.id === 'llave' ? (
                  <span className="text-xs register-option-muted block mt-1">
                    Transfiere a la llave 3192635819. Tu cuenta queda pendiente hasta que un administrador confirme el pago.
                  </span>
                ) : null}
                {m.id === 'pago_sede' ? (
                  <span className="text-xs register-option-muted block mt-1">
                    {sedeLabel
                      ? `Pagarás en ${sedeLabel}. Tu cuenta queda registrada; la sede confirma el pago.`
                      : (isFoodLandingRegister
                        ? 'Pagarás presencialmente. Un administrador Food confirma el pago y activa tu plan.'
                        : 'Pagarás presencialmente en tu gimnasio o sede. La activación la confirma un administrador.')}
                  </span>
                ) : null}
              </button>
            ))}
            </>
            )}
            <FieldNote message={fieldErrors.metodoPago} />
          </div>
          {formData.metodoPago === 'pago_sede' && (
            <div className={`text-xs rounded-lg border p-3 space-y-1 ${
              isFoodLandingRegister
                ? 'food-register-plan-card'
                : 'text-stone-200 border-lime-500/30 bg-lime-950/20'
            }`}>
              <p className={isFoodLandingRegister ? 'font-semibold' : 'font-semibold text-lime-300'}>
                {paymentMethods.find((m) => m.id === 'pago_sede')?.label || 'Pago en sede'}
              </p>
              <p>
                {sedeLabel
                  ? `Acércate a ${sedeLabel} con tu documento. Ya quedarás registrado; el equipo validará el pago y activará tu plan.`
                  : (isFoodLandingRegister
                    ? 'Quedarás registrado. Un administrador Food confirma el pago y activa tu plan. Hasta entonces tu cuenta permanece pendiente.'
                    : 'Acércate a la recepción de tu gimnasio con tu documento. El administrador confirmará el pago y activará tu acceso. Hasta entonces tu cuenta permanece bloqueada.')}
              </p>
            </div>
          )}
          <div className="flex gap-2">
            {isFoodLandingRegister ? null : (
            <button type="button" className="btn-secondary flex-1" onClick={() => setStep(skipTrainingClientPlan ? 2 : 4)}>Atrás</button>
            )}
            <button
              type="submit"
              className={`btn-primary flex-1 ${isFoodLandingRegister ? 'w-full' : ''}`}
              disabled={
                loading
                || Boolean(successMsg)
                || ((!skipMemberPayment && !skipTrainingClientPlan) && (
                  paymentMethodsLoading
                  || !selectedPlan?.nombre
                  || (effectiveService === 'd28d' && cyclesForSelectedPlan.length > 0 && !selectedCycleId)
                ))
              }
            >
              {loading ? 'Procesando…' : (
                skipMemberPayment || skipTrainingClientPlan
                  ? 'Registrarme (sin pago en línea)'
                  : formData.metodoPago === 'pago_sede'
                    ? 'Registrarme (pago pendiente)'
                    : 'Registrarme y pagar'
              )}
            </button>
          </div>
        </form>
      )}

      <p className="text-center text-sm text-stone-500 mt-6">
        ¿Ya tienes cuenta?{' '}
        <button type="button" className="link-button" onClick={onSwitchToLogin}>Iniciar sesión</button>
      </p>
    </AuthLayout>
    {showPrivacyModal ? (
      <PrivacyPolicyModal
        variant={isFoodLandingRegister ? 'food' : 'd28d'}
        onClose={() => setShowPrivacyModal(false)}
        onAccept={() => {
          setAcceptPrivacy(true);
          setShowPrivacyModal(false);
        }}
      />
    ) : null}
    {showTermsModal ? (
      <TermsOfServiceModal
        variant={isFoodLandingRegister ? 'food' : 'd28d'}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          setAcceptTerms(true);
          setShowTermsModal(false);
        }}
      />
    ) : null}
    <RegisterPlanPickerModal
      open={showPlanModal}
      plans={filteredPlans}
      selectedPlan={selectedPlan}
      priceForCurrency={priceForCurrency}
      title="Elige tu plan"
      subtitle={inviteContext?.label || inviteContext?.gym_name || ''}
      onSelect={setSelectedPlan}
      onClose={() => setShowPlanModal(false)}
    />
  </>
  );
}
